/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import {GenericObject} from '@ion/core/src/types/entities';
import {
  EXPORT_COMPLETED,
  EXPORT_CONFIRMED,
  EXPORT_ERROR,
  EXPORT_REQUEST,
  EXPORT_RESPONSE
} from '../actions/exporter';
import {ExportContext} from './../../types/redux';

export interface ExportAction {
  entity: ExportContext,
  message?: string
}

export interface EntityExportState {
  pending: {},
  entity: ExportContext | null;
  isExporting: boolean;
  isComplete: boolean;
  isError: boolean;
  show: boolean;
  message: string;
};

export interface ExportState {
  current: EntityExportState,
}

const defaultState = {
  pending : {
    [ExportContext.asset]                : false,
    [ExportContext.device]               : false,
    [ExportContext.driver]               : false,
    [ExportContext.group]                : false,
    [ExportContext.ioProfile]            : false,
    [ExportContext.role]                 : false,
    [ExportContext.user]                 : false,
    [ExportContext.notification]         : false,
    [ExportContext.schedule]             : false,
    [ExportContext.serviceSchedule]      : false,
    [ExportContext.serviceItem]          : false,
    [ExportContext.maintenanceDashboard] : false,
    [ExportContext.maintenanceSchedule]  : false,
    [ExportContext.maintenanceHistory]   : false,
    [ExportContext.assetHistory]         : false,
    [ExportContext.assetTracking]        : false,
    [ExportContext.geofenceEvents]       : false,
    [ExportContext.crashboxx]            : false,
    [ExportContext.tripList]             : false
  },
  entity      : null,
  isExporting : false,
  isComplete  : false,
  isError     : false,
  show        : false,
  message     : null
};

// defines the initial state for the reducer ...
export const initialState: ExportState = {
  current : {...defaultState}
};

// defines this reducers reducer functions ...
const reducers = {
  [EXPORT_REQUEST] : ({current}: ExportState, {entity}: ExportAction) => {
    current.pending[entity] = true;
    current.entity          = entity;
    current.isExporting     = true;
    current.show            = true;
    current.isComplete      = false;
    current.isError         = false;
    current.message         = null;
  },
  [EXPORT_RESPONSE] : ({current}: ExportState, {entity}: ExportAction) => {
    current.pending[entity] = false;
    current.entity          = entity;
    current.isComplete      = true;
    current.show            = true;
    current.isExporting     = false;
    current.isError         = false;
    current.message         = null;
  },
  [EXPORT_ERROR] : ({current}: ExportState, {entity, message}: ExportAction) => {
    current.pending[entity] = false;
    current.entity          = entity;
    current.isError         = true;
    current.show            = true;
    current.isComplete      = false;
    current.isExporting     = false;
    current.message         = message;
  },
  [EXPORT_CONFIRMED] : ({current}: ExportState) => {
    current.show    = false;
    current.message = null;
  },
  [EXPORT_COMPLETED] : (draft: ExportState) => {
    draft.current = {...defaultState};
  }
};

export const isExporting = (pending: GenericObject, entity: ExportContext): boolean => {
  return pending[entity] === true;
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: ExportState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
